import { type PropsWithChildren } from 'react';
import { ErrorBoundary } from '@toss/error-boundary';
import { SwitchCase } from '@toss/react';
import { logger } from '@zep/core-logger';
import { QuizsetFilterCriteriaSearchType } from '@/api/gen/_model/quizsetFilterCriteriaSearchType';
import { ErrorBoundaryFallback } from '@/components';
import { EnterTracking } from '@/components/EnterTracking';
import { useViewport } from '@/hooks';
import { ProductBottomNavigation, ProductSidebar } from '@/layout/ProductSidebarLayout';
import { FeaturedQuizRollingBanner } from '@/module/featured/shared/FeaturedQuizzesAside/FeaturedQuizRollingBanner';
import { FeaturedQuizzesAside } from '@/module/featured/shared/FeaturedQuizzesAside/FeaturedQuizzesAside';
import { useInitialFilterFromUserInfo, usePublicCategoryFilter } from '@/module/public/hooks';
import { useFetchCategoryByWithAuthUser } from '@/shared/category/useFetchCategoryInfo';
import { PRODUCT_HEADER_HEIGHT, PRODUCT_STICKY_HEIGHT_CLASS, PRODUCT_STICKY_TOP_CLASS } from '@/shared/constants/productLayout.const';
import { useIsInExternalPlatform } from '@/shared/externalPlatform';
import { usePointReleaseModal } from '@/shared/features/classroom/hooks';
import { useIsSmallerThanTabletSm } from '@/shared/hooks';
import { useIsInLti } from '@/shared/lti';
import { LtiNavigationBar } from '@/shared/lti/components/LtiNavigationBar';
import { useSearchQuizQuery } from '@/shared/quizList/hooks/useSearchQuizQuery';
import { ListLayoutContainer, ListLayoutWrapper } from '@/shared/quizList/Layout';
import { SearchInputQuizForm } from '@/shared/quizList/SearchField/';
import { type QuizListSearchProps, SearchMetaBar } from '@/shared/quizList/SearchField/SearchMetaBar';
import { cn } from '@/utils/tailwind.util';
import { NewMapBanner } from '../featured/components/NewMapBanner';
import { QuizList } from './components/QuizList';
import { SearchFilterBar } from './components/SearchFilterBar';
import { SearchScopeDropdown } from './components/SearchScopeDropdown';
import { TotalQuizCount } from './components/TotalQuizCount';
import { usePublicSearchFilterQuery } from './hooks/usePublicSearchFilterQuery';
import { ContentHeader } from './layout/ContentHeader';
export const RightSection = (props: PropsWithChildren<{
  stickyTopGap?: number;
  noBorder?: boolean;
  applyMaxHeight?: boolean;
  className?: string;
}>) => {
  const {
    stickyTopGap,
    noBorder,
    applyMaxHeight,
    children,
    className
  } = props;
  const style = getComputedStickyStyle({
    gap: stickyTopGap,
    applyMaxHeight
  });
  return <div style={style} className={cn('sticky min-w-[300px] max-w-[300px] pl-md pt-sm h-full', noBorder ? 'border-none' : 'border-l border-assistive', className)} data-sentry-component="RightSection" data-sentry-source-file="PublicPageView.tsx">
      {children}
    </div>;
};
const SearchField = (props: QuizListSearchProps) => {
  const {
    searchText,
    handleSearchText,
    handleSearchType
  } = useSearchQuizQuery();
  const {
    clearSearchFilters
  } = usePublicSearchFilterQuery();
  const isSearchMode = Boolean(searchText);
  const exitSearch = () => {
    handleSearchText('');
    clearSearchFilters();
    handleSearchType(QuizsetFilterCriteriaSearchType.ALL);
  };
  return <div className={'flex flex-col items-start justify-between gap-sm pb-xs'} data-sentry-component="SearchField" data-sentry-source-file="PublicPageView.tsx">
      <div className={'flex w-full items-center justify-between gap-xs'}>
        <SearchInputQuizForm {...props} onBackClick={isSearchMode ? exitSearch : undefined} showSearchButton={false} className={'min-w-0 flex-1 tablet-sm:max-w-[800px]'} data-sentry-element="SearchInputQuizForm" data-sentry-source-file="PublicPageView.tsx" />
        <SearchMetaBar {...props} data-sentry-element="SearchMetaBar" data-sentry-source-file="PublicPageView.tsx" />
      </div>
      <div className={'flex w-full items-center gap-3xs overflow-x-auto overflow-y-visible'}>
        {isSearchMode && <SearchScopeDropdown className={'shrink-0'} />}
        <SearchFilterBar className={'w-auto shrink-0 flex-nowrap'} data-sentry-element="SearchFilterBar" data-sentry-source-file="PublicPageView.tsx" />
      </div>
    </div>;
};
const MainSection = (props: {
  availableRollingBanner?: boolean;
}) => {
  const isSmallerThanTabletSm = useIsSmallerThanTabletSm();
  const {
    state: {
      schoolType
    }
  } = usePublicCategoryFilter();
  return <div className="h-auto w-full tablet-sm:flex-[8]" data-sentry-component="MainSection" data-sentry-source-file="PublicPageView.tsx">
      {!isSmallerThanTabletSm && <>
          <SearchField totalCount={<TotalQuizCount />} eventType={'public'} />
          <div className="w-full pb-xs tablet-lg:hidden">
            <NewMapBanner size="sm" />
          </div>
          {props.availableRollingBanner && <FeaturedQuizRollingBanner schoolType={schoolType} />}
        </>}
      <ErrorBoundary onError={e => {
      logger.captureError(e);
    }} renderFallback={args => <ErrorBoundaryFallback {...args} />} data-sentry-element="ErrorBoundary" data-sentry-source-file="PublicPageView.tsx">
        <QuizList data-sentry-element="QuizList" data-sentry-source-file="PublicPageView.tsx" />
      </ErrorBoundary>
    </div>;
};
const ContentContainer = (props: PropsWithChildren<{
  className?: string;
}>) => <main className={cn('relative flex w-full gap-lg', props.className)} data-sentry-component="ContentContainer" data-sentry-source-file="PublicPageView.tsx">
    {props.children}
  </main>;
export type VisibleSectionsType = 'LEFT_MAIN_RIGHT' | 'MAIN_AND_LEFT' | 'MAIN_ONLY';
export const PublicPageView = () => {
  const isSmallerThanTabletSm = useIsSmallerThanTabletSm();
  const {
    initialized,
    state
  } = usePublicCategoryFilter();
  const {
    isPortable
  } = useViewport();
  const isInLti = useIsInLti();
  const isInExternalPlatform = useIsInExternalPlatform();
  const shouldShowProductNavigation = !isInExternalPlatform;
  useFetchCategoryByWithAuthUser();
  useInitialFilterFromUserInfo();
  // 클래스 출시 모달은 포인트 출시 모달로 대체(자동 노출 제거).
  usePointReleaseModal('public_auto_promotion');
  const visibleSections: VisibleSectionsType = (() => {
    if (isSmallerThanTabletSm) return 'MAIN_ONLY';
    if (isPortable) return 'MAIN_AND_LEFT';
    return 'LEFT_MAIN_RIGHT';
  })();
  const content = <>
      <ContentHeader />
      <ContentContainer className={!isSmallerThanTabletSm ? 'gap-[40px]' : undefined}>
        {initialized && <EnterTracking eventName={'enter_public'} eventProperties={{
        ...state
      }} />}
        <SwitchCase caseBy={{
        LEFT_MAIN_RIGHT: <MainSection />,
        MAIN_AND_LEFT: <MainSection availableRollingBanner={true} />,
        MAIN_ONLY: <MainSection />
      }} value={visibleSections} />
      </ContentContainer>
    </>;
  if (!isSmallerThanTabletSm) {
    return <div className="flex w-full bg-default">
        {isInLti && <LtiNavigationBar currentPageKey="lti.nav.publicQuiz" />}
        <div className="flex w-full items-start bg-default">
          {shouldShowProductNavigation && <ProductSidebar style={{
          top: PRODUCT_HEADER_HEIGHT
        }} />}
          <div className="flex min-w-0 flex-1 items-start">
            <div className="flex min-w-0 flex-1 flex-col gap-md px-[24px] py-[12px]">
              {content}
            </div>
            {!isPortable && <ProductRightRail />}
          </div>
        </div>
      </div>;
  }
  return <>
      <ListLayoutWrapper className="pb-[calc(100px+env(safe-area-inset-bottom))]" data-sentry-element="ListLayoutWrapper" data-sentry-source-file="PublicPageView.tsx">
        {isInLti && <LtiNavigationBar currentPageKey="lti.nav.publicQuiz" />}
        <ListLayoutContainer data-sentry-element="ListLayoutContainer" data-sentry-source-file="PublicPageView.tsx">{content}</ListLayoutContainer>
      </ListLayoutWrapper>
      {shouldShowProductNavigation && <ProductBottomNavigation />}
    </>;
};
const ProductRightRail = () => <aside className={cn('sticky w-[340px] shrink-0 overflow-hidden bg-default pl-[16px] pr-[40px] pt-[24px]', PRODUCT_STICKY_TOP_CLASS, PRODUCT_STICKY_HEIGHT_CLASS)} data-sentry-component="ProductRightRail" data-sentry-source-file="PublicPageView.tsx">
    <FeaturedQuizzesAside data-sentry-element="FeaturedQuizzesAside" data-sentry-source-file="PublicPageView.tsx" />
  </aside>;
export const getComputedStickyStyle = ({
  gap = 24,
  applyMaxHeight = false
}: {
  gap?: number;
  applyMaxHeight?: boolean;
}) => {
  const headerHeight = 65;
  const top = headerHeight + gap;
  const maxHeight = `calc(100vh - ${top + gap}px)`;
  return {
    top,
    maxHeight: applyMaxHeight ? maxHeight : undefined
  };
};